import AsyncStorage from '@react-native-async-storage/async-storage';
import type { PilotEvent } from './internalTestPolicy';

export const PILOT_TELEMETRY_STORAGE_KEY = '@dengfeng/pilot-telemetry-v1';

type StorageAdapter = Pick<typeof AsyncStorage, 'getItem' | 'setItem' | 'removeItem'>;
type QueuedPilotEvent = { event: PilotEvent; attempts: number };
type PilotTelemetryState = {
  consentGranted: boolean;
  activatedAt: string | null;
  sessionId: string | null;
  queue: QueuedPilotEvent[];
};

const emptyState = (): PilotTelemetryState => ({
  consentGranted: false,
  activatedAt: null,
  sessionId: null,
  queue: [],
});

const parseState = (raw: string | null): PilotTelemetryState => {
  if (!raw) return emptyState();
  try {
    const value = JSON.parse(raw) as Partial<PilotTelemetryState>;
    return {
      consentGranted: value.consentGranted === true,
      activatedAt: typeof value.activatedAt === 'string' ? value.activatedAt : null,
      sessionId: typeof value.sessionId === 'string' ? value.sessionId : null,
      queue: Array.isArray(value.queue) ? value.queue : [],
    };
  } catch {
    return emptyState();
  }
};

let pilotTelemetryMutationQueue: Promise<void> = Promise.resolve();
let pilotTelemetryGeneration = 0;
let pilotTelemetrySuspended = false;

const serializePilotTelemetryMutation = <T>(
  operation: (generation: number) => Promise<T>
): Promise<T> => {
  const operationGeneration = pilotTelemetryGeneration;
  const run = () => operation(operationGeneration);
  const result = pilotTelemetryMutationQueue.then(run, run);
  pilotTelemetryMutationQueue = result.then(() => undefined, () => undefined);
  return result;
};

export const createPilotTelemetryStore = ({
  storage,
  sendBatch,
  maxQueueSize = 100,
  maxAttempts = 3,
  batchSize = 20,
}: {
  storage: StorageAdapter;
  sendBatch: (events: PilotEvent[]) => Promise<unknown>;
  maxQueueSize?: number;
  maxAttempts?: number;
  batchSize?: number;
}) => {
  const load = async (): Promise<PilotTelemetryState> =>
    parseState(await storage.getItem(PILOT_TELEMETRY_STORAGE_KEY));
  const save = (state: PilotTelemetryState, generation: number): Promise<void> => {
    if (pilotTelemetrySuspended || generation !== pilotTelemetryGeneration) {
      return Promise.resolve();
    }
    return storage.setItem(PILOT_TELEMETRY_STORAGE_KEY, JSON.stringify(state));
  };

  return {
    async activate(
      { activatedAt, sessionId }: { activatedAt: string; sessionId: string },
      initialEvents: PilotEvent[] = []
    ): Promise<void> {
      await serializePilotTelemetryMutation(async (generation) => {
        if (generation !== pilotTelemetryGeneration) return;
        pilotTelemetrySuspended = false;
        const state = await load();
        const existingIds = new Set(state.queue.map((item) => item.event.event_id));
        const queued = initialEvents
          .filter((event) => !existingIds.has(event.event_id))
          .slice(0, Math.max(0, maxQueueSize - state.queue.length))
          .map((event) => ({ event, attempts: 0 }));
        await save({
          ...state,
          consentGranted: true,
          activatedAt,
          sessionId,
          queue: [...state.queue, ...queued],
        }, generation);
      });
    },
    async enqueue(event: PilotEvent): Promise<boolean> {
      return serializePilotTelemetryMutation(async (generation) => {
        try {
          if (pilotTelemetrySuspended || generation !== pilotTelemetryGeneration) return false;
          const state = await load();
          if (!state.consentGranted || state.queue.some((item) => item.event.event_id === event.event_id)) return false;
          if (state.queue.length >= maxQueueSize) return false;
          await save({ ...state, queue: [...state.queue, { event, attempts: 0 }] }, generation);
          if (pilotTelemetrySuspended || generation !== pilotTelemetryGeneration) return false;
          return true;
        } catch {
          return false;
        }
      });
    },
    async flush(): Promise<{ sent: number; retained: number; dropped: number }> {
      return serializePilotTelemetryMutation(async (generation) => {
        let state: PilotTelemetryState;
        try {
          if (pilotTelemetrySuspended || generation !== pilotTelemetryGeneration) {
            return { sent: 0, retained: 0, dropped: 0 };
          }
          state = await load();
        } catch {
          return { sent: 0, retained: 0, dropped: 0 };
        }
        if (!state.consentGranted || state.queue.length === 0) {
          return { sent: 0, retained: state.queue.length, dropped: 0 };
        }
        const batch = state.queue.slice(0, batchSize);
        try {
          await sendBatch(batch.map((item) => item.event));
          if (pilotTelemetrySuspended || generation !== pilotTelemetryGeneration) {
            return { sent: 0, retained: 0, dropped: 0 };
          }
          const next = { ...state, queue: state.queue.slice(batch.length) };
          await save(next, generation);
          return { sent: batch.length, retained: next.queue.length, dropped: 0 };
        } catch {
          if (pilotTelemetrySuspended || generation !== pilotTelemetryGeneration) {
            return { sent: 0, retained: 0, dropped: 0 };
          }
          const attempted = batch.map((item) => ({ ...item, attempts: item.attempts + 1 }));
          const retryable = attempted.filter((item) => item.attempts < maxAttempts);
          const dropped = attempted.length - retryable.length;
          const next = { ...state, queue: [...retryable, ...state.queue.slice(batch.length)] };
          try {
            await save(next, generation);
          } catch {
            return { sent: 0, retained: state.queue.length, dropped: 0 };
          }
          return { sent: 0, retained: next.queue.length, dropped };
        }
      });
    },
    async getContext(): Promise<{ activatedAt: string | null; sessionId: string | null }> {
      if (pilotTelemetrySuspended) return { activatedAt: null, sessionId: null };
      const state = await load();
      return { activatedAt: state.activatedAt, sessionId: state.sessionId };
    },
    async getDebugState(): Promise<{ consentGranted: boolean; queued: number; attempts: number[] }> {
      await pilotTelemetryMutationQueue;
      const state = await load();
      return {
        consentGranted: state.consentGranted,
        queued: state.queue.length,
        attempts: state.queue.map((item) => item.attempts),
      };
    },
    async clear(): Promise<void> {
      pilotTelemetryGeneration += 1;
      pilotTelemetrySuspended = true;
      pilotTelemetryMutationQueue = Promise.resolve();
      await storage.removeItem(PILOT_TELEMETRY_STORAGE_KEY);
    },
  };
};

export const clearPilotTelemetryStorage = (): Promise<void> => {
  pilotTelemetryGeneration += 1;
  pilotTelemetrySuspended = true;
  pilotTelemetryMutationQueue = Promise.resolve();
  return AsyncStorage.removeItem(PILOT_TELEMETRY_STORAGE_KEY);
};
